import produce from "immer";
import {
  COPY_STARTED,
  COPY_QUEUED,
  COPY_SCENARIO_STARTED,
  COPY_SCENARIO_COMPLETED,
  COPY_SCENARIO_FAILED,
  COPY_ALL_COMPLETED,
  COPY_ALL_FAILED,
  COPY_JOB_DISMISS,
  COPY_JOB_MODAL_TOGGLE,
  COPY_REQUEST_FAILED,
} from "../types";

const PROJECT_STATUS = {
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

const initialJobState = {
  active: false,
  open: false,
  startedAt: null,
  terminatedAt: null,
  fileName: null,
  totalProjects: 0,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  avgSecondsPerWeight: null,
  error: null,
  projects: [],
};

const initialState = { jobs: {} };

function upsertProject(job, itemIndex, patch) {
  if (itemIndex == null) return;
  const idx = job.projects.findIndex((p) => p.index === itemIndex);
  if (idx === -1) {
    job.projects.push({
      index: itemIndex,
      label: patch.label ?? "",
      status: PROJECT_STATUS.QUEUED,
      weight: 1,
      startedAt: null,
      completedAt: null,
      error: null,
      ...patch,
    });
    return;
  }
  Object.assign(job.projects[idx], patch);
}

export default function scenarioCopyReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case COPY_STARTED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        // Merge onto an existing job so a re-dispatched STARTED (rehydration)
        // updates the provided fields without zeroing the rest.
        const prev = draft.jobs[jobId];
        draft.jobs[jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          ...payload,
          active: true,
          open: true,
          startedAt: prev?.startedAt ?? payload?.startedAt ?? Date.now(),
        };
      });

    case COPY_QUEUED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = true;
        job.totalProjects = payload.total ?? (payload.projects?.length ?? 0);
        if (payload.avgSecondsPerWeight != null) {
          job.avgSecondsPerWeight = payload.avgSecondsPerWeight;
        }
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        job.projects = (payload.projects || []).map((p) => ({
          index: p.index,
          label: p.label,
          weight: p.weight ?? 1,
          status: PROJECT_STATUS.QUEUED,
          startedAt: null,
          completedAt: null,
          error: null,
        }));
      });

    case COPY_SCENARIO_STARTED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.IN_PROGRESS,
          startedAt: payload.startedAt ?? Date.now(),
        });
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case COPY_SCENARIO_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.COMPLETED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: null,
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case COPY_SCENARIO_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.FAILED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: payload.error || "Unknown error",
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case COPY_ALL_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.etaSeconds = 0;
        job.terminatedAt = Date.now();
      });

    case COPY_ALL_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Scenario copy failed";
        job.terminatedAt = Date.now();
      });

    case COPY_REQUEST_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Copy request failed";
        job.terminatedAt = Date.now();
      });

    case COPY_JOB_MODAL_TOGGLE:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.open = payload?.open ?? !job.open;
      });

    case COPY_JOB_DISMISS:
      return produce(state, (draft) => {
        const jobId = payload?.jobId ?? payload;
        if (jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) delete draft.jobs[id];
          });
        } else {
          delete draft.jobs[jobId];
        }
      });

    default:
      return state;
  }
}

export { PROJECT_STATUS };
